import { Link } from "@tanstack/react-router"
import { ChevronRight, Crown, UserMinus, Users } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { RoomDetail } from "@/api/generated/RoomDetail"
import type { RoomWorker } from "@/api/generated/RoomWorker"
import { useSession } from "@/api/queries/session"
import { useDespawnWorker } from "@/api/queries/workplace"
import { useConfirm } from "@/components/app/confirm-dialog"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { can } from "@/lib/permissions"
import { cn } from "@/lib/utils"
import { WorkerAvatar } from "./room-status"

function WorkerStatus({ worker }: { worker: RoomWorker }) {
  const { t } = useTranslation()
  if (worker.status === "thinking")
    return (
      <StatusBadge tone="accent" dot pulse>
        {t("workplace.room.workers.status.thinking")}
      </StatusBadge>
    )
  if (worker.status === "despawned")
    return <StatusBadge>{t("workplace.room.workers.status.despawned")}</StatusBadge>
  return (
    <StatusBadge tone="success" dot>
      {t("workplace.room.workers.status.idle")}
    </StatusBadge>
  )
}

function WorkerCard({ room, worker }: { room: RoomDetail; worker: RoomWorker }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const despawn = useDespawnWorker(room.id)
  const task = worker.task_id != null ? room.tasks.find((x) => x.id === worker.task_id) : undefined
  const gone = worker.status === "despawned"
  const nextUp = room.next_in_line_worker_id === worker.id
  return (
    <li className={cn("rounded-lg border p-3", gone && "opacity-60")} data-testid="room-worker">
      <div className="flex items-start gap-2.5">
        <WorkerAvatar
          name={worker.display_name}
          leader={worker.is_orchestrator}
          thinking={worker.status === "thinking"}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-[0.8125rem] font-medium">{worker.display_name}</span>
            {worker.is_orchestrator && (
              <StatusBadge tone="warning">
                <Crown />
                {t("workplace.room.workers.leader")}
              </StatusBadge>
            )}
            {nextUp && <StatusBadge tone="info">{t("workplace.room.workers.nextUp")}</StatusBadge>}
          </div>
          <p className="text-muted-foreground truncate text-[0.6875rem]">
            {[
              worker.member_kind === "agent"
                ? (worker.agent_name ?? t("workplace.room.workers.agent"))
                : worker.member_kind === "simple"
                  ? t("workplace.room.workers.simple")
                  : t("workplace.room.workers.formerMember"),
              worker.model_name,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <WorkerStatus worker={worker} />
          {!gone && !worker.is_orchestrator && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={t("workplace.room.workers.despawn", { name: worker.display_name })}
                    onClick={() =>
                      void confirm({
                        title: t("workplace.room.workers.despawnTitle", { name: worker.display_name }),
                        description: t("workplace.room.workers.despawnDescription"),
                        confirmLabel: t("workplace.room.workers.despawnConfirm"),
                        destructive: true,
                        onConfirm: () => despawn.mutateAsync(worker),
                      })
                    }
                  />
                }
              >
                <UserMinus />
              </TooltipTrigger>
              <TooltipContent>{t("workplace.room.workers.despawnConfirm")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      {task && (
        <p className="text-muted-foreground mt-2 truncate text-xs">
          {t("workplace.room.workers.workingOn", { title: `#${task.id} ${task.title}` })}
        </p>
      )}
      {worker.role_system_prompt && (
        <Collapsible className="mt-2">
          <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group/role flex items-center gap-1 text-xs">
            <ChevronRight className="size-3.5 transition-transform group-data-[panel-open]/role:rotate-90" />
            {t("workplace.room.workers.role")}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="text-muted-foreground bg-muted/30 mt-1.5 max-h-48 overflow-y-auto rounded-md p-2 text-xs break-words whitespace-pre-wrap">
              {worker.role_system_prompt}
            </p>
          </CollapsibleContent>
        </Collapsible>
      )}
    </li>
  )
}

export function RoomWorkersPanel({ room }: { room: RoomDetail }) {
  const { t } = useTranslation()
  const session = useSession()
  const active = room.workers.filter((w) => w.status !== "despawned")
  const gone = room.workers.filter((w) => w.status === "despawned")
  if (room.workers.length === 0)
    return <EmptyState variant="plain" size="sm" icon={Users} title={t("workplace.room.workers.empty")} />
  return (
    <div className="space-y-4">
      <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
        <span className="truncate">
          {room.team_name
            ? t("workplace.room.workers.fromTeam", { team: room.team_name })
            : t("workplace.room.workers.noTeam")}
        </span>
        {room.team_id != null && can(session, "management") && (
          <Button
            variant="link"
            size="xs"
            className="h-auto px-0"
            nativeButton={false}
            render={<Link to="/studio/teams" />}
          >
            {t("workplace.room.workers.editTeam")}
          </Button>
        )}
      </div>
      <ul className="space-y-2">
        {active.map((w) => (
          <WorkerCard key={w.id} room={room} worker={w} />
        ))}
      </ul>
      {gone.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-muted-foreground text-[0.6875rem] font-medium tracking-wide uppercase">
            {t("workplace.room.workers.despawnedTitle", { count: gone.length })}
          </h3>
          <ul className="space-y-2">
            {gone.map((w) => (
              <WorkerCard key={w.id} room={room} worker={w} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
